import React, { useState, useEffect } from 'react';
import { FiBookOpen, FiPlus, FiTrash2, FiSave, FiCheck, FiUser, FiFileText } from 'react-icons/fi';
import ImageUploadField from './ImageUploadField';
import './AdminManage.css';

const STORAGE_KEY = 'iamar_revue_rigsida_content';

const DEFAULT_REVUE_DATA = {
  hero: {
    title: "Revue RIGSIDA",
    subtitle: "Revue Internationale de Gouvernance Sécuritaire et Développement en Afrique",
    bgImage: ""
  },
  resume: {
    lead: "La Revue Internationale de Gouvernance Sécuritaire et Développement en Afrique (RIGSIDA) est une revue scientifique à comité de lecture, dédiée à la publication de recherches interdisciplinaires dans les domaines de la gouvernance sécuritaire, de la transformation numérique, du développement durable et des politiques publiques.",
    details: "Elle vise à constituer un espace de dialogue scientifique entre chercheurs, experts et praticiens, en favorisant les approches comparatives et les analyses innovantes dans les contextes africains et euro-méditerranéens."
  },
  director: {
    name: "Dr. Redouane El Majdouli",
    affiliation: "Université Ibn Zohr – Maroc",
    country: "Maroc",
    countryFlag: "🇲🇦",
    photoUrl: "",
    logoUrl: ""
  },
  editorialMembers: [
    { id: 1, name: "Dr. Sarah Johnson", affiliation: "University of Cambridge", country: "Royaume-Uni", countryFlag: "🇬🇧", photoUrl: "", logoUrl: "" },
    { id: 2, name: "Prof. Jean-Claude Martin", affiliation: "Université Paris 1 Panthéon-Sorbonne", country: "France", countryFlag: "🇫🇷", photoUrl: "", logoUrl: "" }
  ]
};

export default function AdminRevueRIGSIDA() {
  const [data, setData] = useState(DEFAULT_REVUE_DATA);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', affiliation: '', country: '', countryFlag: '🇲🇦', photoUrl: '', logoUrl: '' });
  const [showMemberForm, setShowMemberForm] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setData(JSON.parse(saved));
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  const handleAddMember = (e) => {
    e.preventDefault();
    if (!newMember.name) return;
    setData(prev => ({
      ...prev,
      editorialMembers: [...prev.editorialMembers, { ...newMember, id: Date.now() }]
    }));
    setNewMember({ name: '', affiliation: '', country: '', countryFlag: '🇲🇦', photoUrl: '', logoUrl: '' });
    setShowMemberForm(false);
  };

  const handleDeleteMember = (id) => {
    setData(prev => ({
      ...prev,
      editorialMembers: prev.editorialMembers.filter(m => m.id !== id)
    }));
  };

  return (
    <div className="admin-manage-card" style={{ background: '#ffffff', borderRadius: 8, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: 16, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#1b1e4f', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiBookOpen /> Gestion de la Revue RIGSIDA
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.88rem' }}>
            Personnalisez le bandeau, le résumé scientifique et le comité éditorial de la revue RIGSIDA
          </p>
        </div>

        <button
          onClick={handleSave}
          style={{
            background: saveSuccess ? '#10b981' : '#1b1e4f',
            color: 'white',
            border: 'none',
            padding: '9px 20px',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          {saveSuccess ? <FiCheck size={16} /> : <FiSave size={16} />}
          {saveSuccess ? 'Enregistré avec succès !' : 'Enregistrer les modifications'}
        </button>
      </div>

      {/* Hero Section */}
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 24, background: '#f8fafc' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#1e293b' }}>
          1. Bannière d'en-tête (Hero Banner)
        </h3>

        <ImageUploadField
          label="Image de fond de la bannière Hero (Upload image)"
          value={data.hero.bgImage}
          onChange={(url) => setData(prev => ({ ...prev, hero: { ...prev.hero, bgImage: url } }))}
          helpText="Glissez ou téléversez l'image d'arrière-plan de la revue RIGSIDA"
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Titre principal</label>
            <input
              type="text"
              value={data.hero.title}
              onChange={(e) => setData(prev => ({ ...prev, hero: { ...prev.hero, title: e.target.value } }))}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Sous-titre descriptif</label>
            <input
              type="text"
              value={data.hero.subtitle}
              onChange={(e) => setData(prev => ({ ...prev, hero: { ...prev.hero, subtitle: e.target.value } }))}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      </div>

      {/* Résumé Scientifique */}
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 24, background: '#ffffff' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#1e293b' }}>
          2. Résumé Scientifique de la Revue
        </h3>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Paragraphe introductif (Lead)</label>
          <textarea
            rows={3}
            value={data.resume.lead}
            onChange={(e) => setData(prev => ({ ...prev, resume: { ...prev.resume, lead: e.target.value } }))}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Détails et vocation de la revue</label>
          <textarea
            rows={3}
            value={data.resume.details}
            onChange={(e) => setData(prev => ({ ...prev, resume: { ...prev.resume, details: e.target.value } }))}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
          />
        </div>
      </div>

      {/* Directeur de la Revue */}
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 24, background: '#f8fafc' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#1e293b' }}>
          3. Direction de la Revue (Directeur de publication)
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <ImageUploadField
            label="Portrait officiel du Directeur (Upload image)"
            value={data.director.photoUrl}
            onChange={(url) => setData(prev => ({ ...prev, director: { ...prev.director, photoUrl: url } }))}
            helpText="Importez la photo du Directeur de la Revue"
          />

          <ImageUploadField
            label="Logo de l'Institution affiliée (Upload image)"
            value={data.director.logoUrl}
            onChange={(url) => setData(prev => ({ ...prev, director: { ...prev.director, logoUrl: url } }))}
            helpText="Importez le logo de l'université ou institution"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Nom du Directeur</label>
            <input
              type="text"
              value={data.director.name}
              onChange={(e) => setData(prev => ({ ...prev, director: { ...prev.director, name: e.target.value } }))}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Affiliation académique</label>
            <input
              type="text"
              value={data.director.affiliation}
              onChange={(e) => setData(prev => ({ ...prev, director: { ...prev.director, affiliation: e.target.value } }))}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Drapeau / Émoji</label>
            <input
              type="text"
              value={data.director.countryFlag}
              onChange={(e) => setData(prev => ({ ...prev, director: { ...prev.director, countryFlag: e.target.value } }))}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      </div>

      {/* Membres du Comité Éditorial */}
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, background: '#ffffff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>
            4. Membres du Comité Éditorial ({data.editorialMembers.length})
          </h3>
          <button
            onClick={() => setShowMemberForm(true)}
            style={{
              background: '#1b1e4f',
              color: 'white',
              border: 'none',
              padding: '7px 14px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <FiPlus size={14} /> Ajouter un membre
          </button>
        </div>

        {showMemberForm && (
          <form onSubmit={handleAddMember} style={{ background: '#f8fafc', padding: 16, borderRadius: 8, border: '1px solid #cbd5e1', marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#1e293b' }}>Nouveau membre du comité éditorial</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <ImageUploadField
                label="Photo du membre (Upload image)"
                value={newMember.photoUrl}
                onChange={(url) => setNewMember(prev => ({ ...prev, photoUrl: url }))}
              />
              <ImageUploadField
                label="Logo institution (Upload image)"
                value={newMember.logoUrl}
                onChange={(url) => setNewMember(prev => ({ ...prev, logoUrl: url }))}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginTop: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Nom complet *</label>
                <input
                  type="text"
                  required
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Affiliation / Université *</label>
                <input
                  type="text"
                  required
                  value={newMember.affiliation}
                  onChange={(e) => setNewMember({ ...newMember, affiliation: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Drapeau (Émoji, ex: 🇬🇧, 🇫🇷)</label>
                <input
                  type="text"
                  value={newMember.countryFlag}
                  onChange={(e) => setNewMember({ ...newMember, countryFlag: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button
                type="button"
                onClick={() => setShowMemberForm(false)}
                style={{ background: '#e2e8f0', color: '#475569', border: 'none', padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                Annuler
              </button>
              <button
                type="submit"
                style={{ background: '#1b1e4f', color: 'white', border: 'none', padding: '7px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                Ajouter
              </button>
            </div>
          </form>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
          {data.editorialMembers.map(m => (
            <div key={m.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', background: '#e2e8f0', flexShrink: 0 }}>
                  {m.photoUrl ? (
                    <img src={m.photoUrl} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                      <FiUser size={18} />
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <h5 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{m.name}</h5>
                    <span>{m.countryFlag}</span>
                  </div>
                  <p style={{ margin: '2px 0 0 0', fontSize: 11, color: '#64748b' }}>{m.affiliation}</p>
                </div>
              </div>

              <button
                onClick={() => handleDeleteMember(m.id)}
                style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 6 }}
                title="Supprimer"
              >
                <FiTrash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
